/**
 * The feed drawer's URL grammar.
 *
 *   /dashboard/feed/<node_id>?asset=<machine_node_id>&event=<kind>:<source_id>&tab=history
 *
 * `asset` is optional, because not every feed item belongs to one — a report is filed
 * against a group, not a machine. Such a link names only its `event`, and the feed marks
 * that card in the list instead of opening a drawer there is no asset for.
 *
 * This exists so a push notification can land on a specific event card: the server
 * composes the whole destination (`notification-experience.md` §3, and the payload's
 * `data.landing_url`), and the client only has to be able to honour it. `asset` and `event`
 * are separate because a feed item names its machine (`node_id`) independently of the
 * node the feed itself is scoped to — an alarm on a machine shows up in its plant's
 * feed, and the drawer opens on the machine, not the plant.
 *
 * `event` is the feed item's identity, `${type}:${source_id}` — the same string
 * `feedItemKey` builds and the same shape chat's feed pointer uses (`pointerKey` in
 * src/chat/core/reference). Backend already has both halves: they are the `type` and
 * `source_id` that `/api/feed` returns.
 *
 * The params are the drawer's source of truth while they are present, which is what
 * makes closing stick: the close handler strips them, so nothing reopens the drawer on
 * the next render or on a refresh. Opening the drawer by CLICKING a card deliberately
 * does not write them — the feed's route stays on the listed node (see `selectionDuck`),
 * and only a deep link needs to be reproducible from the URL.
 */

export interface FeedDrawerUrlTarget {
    /** null when the item belongs to no asset, so there is no drawer to open. */
    assetId: number | null;
    /** null when the link names an asset but no particular card. */
    eventKey: string | null;
    /** Meaningless without an asset; the drawer is the only thing that has tabs. */
    tab: 'detail' | 'history';
}

/** Query keys this module owns. Anything else in the search string is left alone. */
export const FEED_DRAWER_PARAMS = ['asset', 'event', 'tab'] as const;

// A feed item's identity: an open kind string (the registry is add-only and lives
// server-side, so the client must not enumerate it) followed by a positive source id.
/** A feed item's identity, `${type}:${source_id}`. */
export const EVENT_KEY = /^[A-Za-z][A-Za-z0-9_]*:[1-9]\d*$/;

const asPositiveInt = (value: string | null): number | null => {
    if (value === null || !/^[1-9]\d*$/.test(value)) return null;
    return Number(value);
};

/**
 * Read the drawer target out of a location search string, or null when the link names
 * neither a usable asset nor a usable event.
 *
 * An `asset` that is present but malformed reads the same as an absent one: either way
 * there is no drawer to open, since one keyed to a nonsense id would render empty. A
 * link that still names a valid event degrades to marking that card rather than being
 * discarded whole.
 */
export const parseDrawerTarget = (search: string): FeedDrawerUrlTarget | null => {
    const params = new URLSearchParams(search);

    const assetId = asPositiveInt(params.get('asset'));

    const rawEvent = params.get('event');
    const eventKey = rawEvent !== null && EVENT_KEY.test(rawEvent) ? rawEvent : null;

    if (assetId === null && eventKey === null) return null;

    // A link that names a card is about that card, so it defaults to the tab that shows
    // it; a link that names only an asset opens on the asset itself. An explicit `tab`
    // wins, and an unrecognized one falls back rather than rejecting the whole link.
    const rawTab = params.get('tab');
    const tab = rawTab === 'detail' || rawTab === 'history' ? rawTab : eventKey ? 'history' : 'detail';

    return { assetId, eventKey, tab };
};

/**
 * The same search string without the drawer's params, ready for a history.replace.
 * Returns '' when nothing is left, so the caller writes a bare path rather than a
 * trailing '?'.
 */
export const stripDrawerParams = (search: string): string => {
    const params = new URLSearchParams(search);
    FEED_DRAWER_PARAMS.forEach((key) => params.delete(key));
    const rest = params.toString();
    return rest ? `?${rest}` : '';
};

/** Whether a search string addresses the drawer at all — cheaper than a full parse. */
export const hasDrawerParams = (search: string): boolean =>
    FEED_DRAWER_PARAMS.some((key) => new URLSearchParams(search).has(key));

/**
 * What a change to the search string should do to the drawer.
 *
 * A separate decision from `parseDrawerTarget` because "the URL names no drawer target" is not
 * the same as "close the drawer": a drawer opened by clicking a card writes no params, so the URL
 * is not entitled to close it. `openedFromUrl` is what tells the two apart, and without it the
 * effect that consumes this would shut a reader's drawer whenever it happened to re-run.
 */
export type FeedDrawerAction =
    | { kind: 'ignore' }
    | { kind: 'clear' }
    | { kind: 'open'; assetId: number; eventKey: string | null; tab: 'detail' | 'history' }
    /** An event that belongs to no asset: mark the card, and close a drawer the URL has left behind. */
    | { kind: 'markOnly'; eventKey: string | null };

export const drawerActionFor = (search: string, openedFromUrl: boolean): FeedDrawerAction => {
    const target = parseDrawerTarget(search);

    if (target === null) {
        return openedFromUrl ? { kind: 'clear' } : { kind: 'ignore' };
    }
    if (target.assetId !== null) {
        return { kind: 'open', assetId: target.assetId, eventKey: target.eventKey, tab: target.tab };
    }
    return { kind: 'markOnly', eventKey: target.eventKey };
};

/** The region of a feed card a click landed on. */
export type FeedCardRegion = 'identity' | 'body';

/**
 * Which tab a click on a feed card opens.
 *
 * With a pointer the identity line is a target of its own, so clicking the asset's name opens the
 * asset itself and the rest of the card opens the history with that event expanded. A finger has
 * no such target: on a narrow screen the identity line covers most of the card, and a tap meant
 * for the card lands on it. There both regions open the history.
 */
export const feedCardTab = (region: FeedCardRegion, touchLayout: boolean): 'detail' | 'history' =>
    region === 'identity' && !touchLayout ? 'detail' : 'history';
